/** Singapore NEA PSI and PM2.5 client for the official Data.gov.sg API. */
import { getTierForPsi, type AirTier } from './tiers.ts';

export type RegionId = 'central' | 'north' | 'south' | 'east' | 'west';
export const REGION_IDS: readonly RegionId[] = ['central', 'north', 'south', 'east', 'west'];
export const IS_DEBUG_MOCK =
  import.meta.env?.DEV === true && import.meta.env.VITE_USE_MOCK_PSI === 'true';

export interface RegionReading {
  region: RegionId;
  psi: number | null;
}

export interface PsiData {
  timestamp: string | null;
  updateTimestamp: string | null;
  regions: RegionReading[];
  pm25ByRegion: Partial<Record<RegionId, number | null>>;
  reading: { psi: number; pm25: number | null; tier: AirTier };
}

export const API_ENDPOINT = 'https://api-open.data.gov.sg/v2/real-time/api/psi';
type RegionalValues = Partial<Record<RegionId, number | null>>;
export interface PsiApiPayload {
  code?: number;
  errorMsg?: string;
  data?: {
    items?: Array<{
      date?: string;
      timestamp?: string;
      updatedTimestamp?: string;
      readings?: {
        psi_twenty_four_hourly?: RegionalValues;
        pm25_twenty_four_hourly?: RegionalValues;
      };
    }>;
  };
}

function finiteReading(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function validTimestamp(value: unknown): string | null {
  return typeof value === 'string' && /T/.test(value) && Number.isFinite(Date.parse(value))
    ? value
    : null;
}

/** Normalize the API response once for both the live feed and development mock. */
export function parsePsiData(payload: PsiApiPayload): PsiData {
  if (payload?.code !== 0) {
    throw new Error(payload?.errorMsg || 'PSI API returned an unsuccessful response');
  }
  const item = payload.data?.items?.[0];
  if (!item?.readings) throw new Error('Malformed API response structure');

  const psiValues = item.readings.psi_twenty_four_hourly ?? {};
  const pm25Values = item.readings.pm25_twenty_four_hourly ?? {};
  const regions = REGION_IDS.map((region) => ({ region, psi: finiteReading(psiValues[region]) }));
  const availablePsi = regions.map(({ psi }) => psi).filter((psi): psi is number => psi !== null);
  if (!availablePsi.length) throw new Error('PSI readings are missing from the API response');

  const psi = Math.max(...availablePsi);
  const availablePm25 = REGION_IDS.map((region) => finiteReading(pm25Values[region])).filter(
    (value): value is number => value !== null,
  );
  const pm25 = availablePm25.length
    ? Math.round(availablePm25.reduce((sum, value) => sum + value, 0) / availablePm25.length)
    : null;
  return {
    timestamp: validTimestamp(item.timestamp),
    updateTimestamp: validTimestamp(item.updatedTimestamp) ?? validTimestamp(item.timestamp),
    regions,
    pm25ByRegion: Object.fromEntries(
      REGION_IDS.map((region) => [region, finiteReading(pm25Values[region])]),
    ),
    reading: { psi, pm25, tier: getTierForPsi(psi) },
  };
}

export async function fetchLivePsi(signal?: AbortSignal): Promise<PsiData> {
  if (IS_DEBUG_MOCK) {
    const { MOCK_PSI_BY_REGION, MOCK_PM25_BY_REGION } = await import('./mockPsi');
    const now = new Date().toISOString();
    return parsePsiData({
      code: 0,
      data: {
        items: [
          {
            timestamp: now,
            readings: {
              psi_twenty_four_hourly: MOCK_PSI_BY_REGION,
              pm25_twenty_four_hourly: MOCK_PM25_BY_REGION,
            },
          },
        ],
      },
    });
  }

  return parsePsiData(await fetchPsiPayload(API_ENDPOINT, signal));
}

/** Shared cancellation and timeout for live and historical requests. */
export async function fetchPsiPayload(
  url: string,
  signal?: AbortSignal,
  priority: RequestPriority = 'auto',
): Promise<PsiApiPayload> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timeout = setTimeout(abort, 6000);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      priority,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`API returned status ${response.status}`);
    const payload = (await response.json()) as PsiApiPayload;
    if (payload?.code !== 0)
      throw new Error(payload?.errorMsg || 'PSI API returned an unsuccessful response');
    return payload;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
